import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  INSPECTION_SCALE_KEYS,
  INSPECTION_SCALE_LABELS,
  inspectionTemplateItemScale,
  type InspectionScaleKey,
  type InspectionTemplateItem,
  type InspectionTemplateSection,
} from '@garage/shared'
import {
  PageHeader,
  Button,
  TextField,
  Select,
  Dialog,
  ConfirmDialog,
  EmptyState,
  Skeleton,
  useSnackbar,
  AddIcon,
  EditIcon,
  DeleteIcon,
  ChevronDownIcon,
  ChevronRightIcon,
} from '@/ui'
import { api, ApiError } from '@/lib/api.js'
import { qk } from '@/lib/query-keys.js'

type SectionDialogState = { open: false } | { open: true; section?: InspectionTemplateSection }
type ItemDialogState = { open: false } | { open: true; sectionId: string; item?: InspectionTemplateItem }
type DeleteTarget =
  | { kind: 'section'; section: InspectionTemplateSection }
  | { kind: 'item'; item: InspectionTemplateItem }
  | null

/**
 * Settings entry point for the vehicle inspection checklist: add, rename, or
 * remove sections and items, and pick each item's rating scale. Same
 * rack/level editing pattern as the Storage page. Already-recorded
 * inspections reference items by id, so renaming or removing one here never
 * touches what was already saved against an older car.
 */
export function InspectionTemplatePage() {
  const queryClient = useQueryClient()
  const snackbar = useSnackbar()
  const { t } = useTranslation()

  const scaleLabel = (key: InspectionScaleKey) => t(`inspection.scaleLabels.${key}`, INSPECTION_SCALE_LABELS[key])

  const { data, isLoading, isError } = useQuery({
    queryKey: qk.inspectionTemplate,
    queryFn: api.inspectionTemplate.tree,
  })
  const sections = data?.items ?? []

  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const [sectionDialog, setSectionDialog] = useState<SectionDialogState>({ open: false })
  const [itemDialog, setItemDialog] = useState<ItemDialogState>({ open: false })
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget>(null)

  const toggleSection = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.inspectionTemplate.removeNode(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.inspectionTemplate })
      snackbar.success(t('inspectionTemplate.removed'))
      setDeleteTarget(null)
    },
    onError: (error) => snackbar.error(error instanceof ApiError ? error.message : t('inspectionTemplate.failedToRemove')),
  })

  if (isLoading) {
    return (
      <div className="space-y-6">
        <PageHeader title={t('inspectionTemplate.title')} back={{ to: '/settings', label: t('settings.title') }} />
        <Skeleton lines={6} />
      </div>
    )
  }

  if (isError) {
    return (
      <div className="space-y-6">
        <PageHeader title={t('inspectionTemplate.title')} back={{ to: '/settings', label: t('settings.title') }} />
        <EmptyState title={t('inspectionTemplate.couldntLoad')} description={t('inspectionTemplate.tryRefreshing')} />
      </div>
    )
  }

  return (
    <div className="space-y-6 pb-24">
      <PageHeader
        title={t('inspectionTemplate.title')}
        subtitle={t('inspectionTemplate.subtitle')}
        back={{ to: '/settings', label: t('settings.title') }}
        actions={
          <Button variant="filled" icon={<AddIcon className="w-5 h-5" />} onClick={() => setSectionDialog({ open: true })}>
            {t('inspectionTemplate.addSection')}
          </Button>
        }
      />

      {sections.length === 0 ? (
        <EmptyState
          title={t('inspection.noChecklistYet')}
          description={t('inspectionTemplate.addSectionToStart')}
          action={
            <Button variant="tonal" onClick={() => setSectionDialog({ open: true })}>
              {t('inspectionTemplate.addASection')}
            </Button>
          }
        />
      ) : (
        <div className="space-y-3">
          {sections.map((section) => {
            const isOpen = expanded.has(section.id)
            return (
              <div key={section.id} className="bg-surface-container border border-outline-variant rounded-lg overflow-hidden">
                <div className="w-full flex items-center justify-between gap-3 p-4">
                  <button
                    type="button"
                    onClick={() => toggleSection(section.id)}
                    className="flex items-center gap-3 min-w-0 flex-1 text-start interactive"
                    aria-expanded={isOpen}
                  >
                    {isOpen ? (
                      <ChevronDownIcon className="w-5 h-5 flex-shrink-0 text-on-surface-variant" />
                    ) : (
                      <ChevronRightIcon className="w-5 h-5 flex-shrink-0 text-on-surface-variant" />
                    )}
                    <span className="text-title-md text-on-surface truncate">{section.label}</span>
                    <span className="text-body-sm text-on-surface-variant flex-shrink-0">
                      {t('inspectionTemplate.itemCount', { count: section.items.length })}
                    </span>
                  </button>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      type="button"
                      aria-label={t('inspectionTemplate.editSection', { section: section.label })}
                      onClick={() => setSectionDialog({ open: true, section })}
                      className="p-2 rounded-md text-on-surface-variant hover:text-on-surface hover:bg-surface-high interactive"
                    >
                      <EditIcon className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      aria-label={t('inspectionTemplate.removeSection', { section: section.label })}
                      onClick={() => setDeleteTarget({ kind: 'section', section })}
                      className="p-2 rounded-md text-on-surface-variant hover:text-error hover:bg-surface-high interactive"
                    >
                      <DeleteIcon className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {isOpen && (
                  <div className="px-4 pb-4 space-y-2 border-t border-outline-variant pt-3">
                    {section.items.map((item) => (
                      <div
                        key={item.id}
                        className="flex items-center justify-between gap-3 py-2 px-3 rounded-md bg-surface-low"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="text-body-md text-on-surface truncate">{item.label}</div>
                          <div className="text-body-sm text-on-surface-variant">
                            {scaleLabel(inspectionTemplateItemScale(item))}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <button
                            type="button"
                            aria-label={t('inspectionTemplate.editItem', { item: item.label })}
                            onClick={() => setItemDialog({ open: true, sectionId: section.id, item })}
                            className="p-2 rounded-md text-on-surface-variant hover:text-on-surface hover:bg-surface-high interactive"
                          >
                            <EditIcon className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            aria-label={t('inspectionTemplate.removeItem', { item: item.label })}
                            onClick={() => setDeleteTarget({ kind: 'item', item })}
                            className="p-2 rounded-md text-on-surface-variant hover:text-error hover:bg-surface-high interactive"
                          >
                            <DeleteIcon className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}

                    <Button
                      variant="outlined"
                      size="sm"
                      icon={<AddIcon className="w-4 h-4" />}
                      onClick={() => setItemDialog({ open: true, sectionId: section.id })}
                    >
                      {t('inspectionTemplate.addItem')}
                    </Button>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {sectionDialog.open && (
        <SectionFormDialog open section={sectionDialog.section} onClose={() => setSectionDialog({ open: false })} />
      )}

      {itemDialog.open && (
        <ItemFormDialog
          open
          sectionId={itemDialog.sectionId}
          item={itemDialog.item}
          onClose={() => setItemDialog({ open: false })}
        />
      )}

      <ConfirmDialog
        open={!!deleteTarget}
        title={deleteTarget?.kind === 'section' ? t('inspectionTemplate.removeSectionTitle') : t('inspectionTemplate.removeItemTitle')}
        message={
          deleteTarget?.kind === 'section'
            ? t('inspectionTemplate.removeSectionMessage', { section: deleteTarget.section.label, count: deleteTarget.section.items.length })
            : deleteTarget?.kind === 'item'
              ? t('inspectionTemplate.removeItemMessage', { item: deleteTarget.item.label })
              : ''
        }
        confirmLabel={t('common.remove')}
        cancelLabel={t('common.cancel')}
        destructive
        loading={deleteMutation.isPending}
        onConfirm={() => {
          if (deleteTarget?.kind === 'section') deleteMutation.mutate(deleteTarget.section.id)
          else if (deleteTarget?.kind === 'item') deleteMutation.mutate(deleteTarget.item.id)
        }}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  )
}

function SectionFormDialog({
  open,
  section,
  onClose,
}: {
  open: boolean
  section?: InspectionTemplateSection
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const snackbar = useSnackbar()
  const { t } = useTranslation()
  const isCreate = !section
  const [label, setLabel] = useState(section?.label ?? '')
  const [error, setError] = useState<string | undefined>()

  const mutation = useMutation({
    mutationFn: () =>
      isCreate
        ? api.inspectionTemplate.createNode(null, { label, scale: null, position: 0 })
        : api.inspectionTemplate.updateNode(section!.id, { label, scale: null, position: section!.position }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.inspectionTemplate })
      snackbar.success(isCreate ? t('inspectionTemplate.sectionAdded') : t('inspectionTemplate.sectionUpdated'))
      onClose()
    },
    onError: (err) => {
      if (err instanceof ApiError) setError(err.message)
      snackbar.error(err instanceof ApiError ? err.message : t('common.errorOccurred'))
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!label.trim()) {
      setError(t('inspectionTemplate.nameRequired'))
      return
    }
    setError(undefined)
    mutation.mutate()
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={isCreate ? t('inspectionTemplate.addSection') : t('inspectionTemplate.editSectionTitle')}
      size="sm"
      dismissible={false}
      actions={
        <div className="flex gap-3 justify-end">
          <Button variant="outlined" onClick={onClose} disabled={mutation.isPending}>
            {t('common.cancel')}
          </Button>
          <Button variant="filled" onClick={handleSubmit} loading={mutation.isPending}>
            {isCreate ? t('common.add') : t('common.save')}
          </Button>
        </div>
      }
    >
      <form onSubmit={handleSubmit}>
        <TextField label={t('inspectionTemplate.name')} value={label} onChange={setLabel} error={error} fullWidth autoFocus />
      </form>
    </Dialog>
  )
}

function ItemFormDialog({
  open,
  sectionId,
  item,
  onClose,
}: {
  open: boolean
  sectionId: string
  item?: InspectionTemplateItem
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const snackbar = useSnackbar()
  const { t } = useTranslation()
  const isCreate = !item
  const [label, setLabel] = useState(item?.label ?? '')
  const [scale, setScale] = useState<InspectionScaleKey>(item ? inspectionTemplateItemScale(item) : 'condition')
  const [error, setError] = useState<string | undefined>()

  const scaleOptions = INSPECTION_SCALE_KEYS.map((key) => ({
    value: key,
    label: t(`inspection.scaleLabels.${key}`, INSPECTION_SCALE_LABELS[key]),
  }))

  const mutation = useMutation({
    mutationFn: () =>
      isCreate
        ? api.inspectionTemplate.createNode(sectionId, { label, scale, position: 0 })
        : api.inspectionTemplate.updateNode(item!.id, { label, scale, position: item!.position }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: qk.inspectionTemplate })
      snackbar.success(isCreate ? t('inspectionTemplate.itemAdded') : t('inspectionTemplate.itemUpdated'))
      onClose()
    },
    onError: (err) => {
      if (err instanceof ApiError) setError(err.message)
      snackbar.error(err instanceof ApiError ? err.message : t('common.errorOccurred'))
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!label.trim()) {
      setError(t('inspectionTemplate.nameRequired'))
      return
    }
    setError(undefined)
    mutation.mutate()
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={isCreate ? t('inspectionTemplate.addItem') : t('inspectionTemplate.editItemTitle')}
      size="sm"
      dismissible={false}
      actions={
        <div className="flex gap-3 justify-end">
          <Button variant="outlined" onClick={onClose} disabled={mutation.isPending}>
            {t('common.cancel')}
          </Button>
          <Button variant="filled" onClick={handleSubmit} loading={mutation.isPending}>
            {isCreate ? t('common.add') : t('common.save')}
          </Button>
        </div>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <TextField label={t('inspectionTemplate.name')} value={label} onChange={setLabel} error={error} fullWidth autoFocus />
        <Select
          label={t('inspectionTemplate.ratingScale')}
          value={scale}
          onChange={(v) => setScale(v as InspectionScaleKey)}
          options={scaleOptions}
          fullWidth
        />
      </form>
    </Dialog>
  )
}
